import { useState, useEffect, useCallback } from 'react';
import { Percomatic } from '@biggby-coffee/percomatic-typescript';
import { useRecoilValue } from 'recoil';

import {
	useDecreaseModifierQuantity,
	useIncreaseModifierQuantity
} from '../../hooks/api';
import { modifyingState, transModifiersState } from '../../atoms';
import { normalizeLabel } from '../../../utils/normalizeLabel';
import PlusSVG from '../../../assets/imgs/icon/plus-solid.svg';
import MinusSVG from '../../../assets/imgs/icon/minus-solid.svg';
export type ModifierButtonProps = {
	modifier: Percomatic.Serialized.Modifier;
	modifierGroupIndex: number;
	modifierIndex: number;
	buttonClickHandler: (index: number) => () => void;
	isActive: boolean;
};

export const ModifierButton = ({
	modifier,
	modifierGroupIndex,
	buttonClickHandler,
	modifierIndex,
	isActive
}: ModifierButtonProps): JSX.Element => {
	const decreaseQuantity = useDecreaseModifierQuantity();
	const increaseQuantity = useIncreaseModifierQuantity();

	const modifiers = useRecoilValue(transModifiersState);
	const modifying = useRecoilValue(modifyingState);

	const [quantity, setQuantity] = useState(0);

	useEffect(() => {
		if (modifiers) {
			const count = modifiers.reduce(
				(acc, { modifier: { id } }) =>
					acc + (id === modifier.id ? 1 : 0),
				0
			);

			setQuantity(count);
		}
	}, [modifiers, modifier.id]);

	const determineClickable = useCallback(() => {
		if (modifier.isVariableQuantity) {
			if (quantity <= 0) {
				return true;
			}

			return false;
		}

		return true;
	}, [modifier.isVariableQuantity, quantity]);

	return (
		<div>
			<button
				className={`product-modifier-button btn btn-${
					isActive ? 'primary' : 'white'
				} text-${isActive ? 'white' : 'black'} btn-block w-100${
					modifying ? ' modifying' : ''
				}`}
				style={
					isActive
						? {
								borderRadius: '4px',
								color: '#222222',
								border: 'transparent'
							}
						: {
								borderRadius: '4px',
								color: '#222222',
								border: 'solid 2px #b6b6b6'
							}
				}
				onClick={() =>
					determineClickable()
						? buttonClickHandler(modifierIndex)()
						: null
				}
				data-name={normalizeLabel(modifier.name)} // useful for e2e testing
			>
				<div className="variable-qty-control">
					{modifier.isVariableQuantity && isActive && (
						<img
							src={MinusSVG}
							onClick={e => {
								e.stopPropagation();
								decreaseQuantity(
									modifierGroupIndex,
									modifierIndex
								);
							}}
							style={{ width: '14px', height: '14px' }}
						/>
					)}

					<div
						className="d-flex align-items-center justify-content-center"
						style={{
							minWidth: '80%'
						}}
					>
						<div
							className={`text-break${
								quantity > 0 && modifier.isVariableQuantity
									? ' mr-1'
									: ''
							}`}
						>
							{modifier.name}
						</div>

						{quantity > 0 && modifier.isVariableQuantity && (
							<div
								className="badge badge-pill badge-dark text-light"
								style={{
									top: 0,
									minWidth: '2rem'
								}}
							>
								{quantity}
							</div>
						)}
					</div>

					{modifier.isVariableQuantity && isActive && (
						<img
							src={PlusSVG}
							onClick={e => {
								e.stopPropagation();
								increaseQuantity(
									modifierGroupIndex,
									modifierIndex
								);
							}}
							style={{ width: '14px', height: '14px' }}
						/>
					)}
				</div>
			</button>
		</div>
	);
};
